
import { useEffect } from 'react';
import { useRSVP } from '../context/RSVPContext';
import { useEventData } from './useEventData';
import { useGuestData } from './useGuestData';
import { useRSVPUpdatesAllowed } from './useRSVPUpdatesAllowed';
import { useUserRSVPData } from './useUserRSVPData';

export const useUpdateRSVP = (eventId: string) => {
  const { 
    setEventData,
    setAvailableGuests,
    setRespondedGuests,
    setInProgressGuests,
  } = useRSVP();

  // Load event data
  const { 
    eventData, 
    isLoading: isLoadingEvent,
    error: eventError
  } = useEventData(eventId);
  
  // Check if RSVP updates are allowed
  const {
    updatesAllowed,
    checkingUpdatesAllowed
  } = useRSVPUpdatesAllowed(eventId);
  
  // Fetch user's existing RSVP data
  const {
    fetchingUserRSVP,
    fetchError
  } = useUserRSVPData(eventId);
  
  // Fetch guest data using the useGuestData hook
  const {
    availableGuests,
    respondedGuests,
    inProgressGuests,
    isLoading: isLoadingGuests,
    error: guestError
  } = useGuestData(eventId);
  
  // Update context with guest data when it's available
  useEffect(() => {
    if (availableGuests.length > 0 || respondedGuests.length > 0 || inProgressGuests.length > 0) {
      setAvailableGuests(availableGuests);
      setRespondedGuests(respondedGuests);
      setInProgressGuests(inProgressGuests);
    }
  }, [availableGuests, respondedGuests, inProgressGuests, setAvailableGuests, setRespondedGuests, setInProgressGuests]);

  // Update context with fetched event data
  useEffect(() => {
    if (eventData) {
      setEventData(eventData);
    }
  }, [eventData, setEventData]);

  return {
    eventData,
    isLoadingEvent,
    eventError,
    fetchingUserRSVP,
    fetchError,
    checkingUpdatesAllowed,
    updatesAllowed,
    availableGuests,
    respondedGuests,
    inProgressGuests,
    isLoading: isLoadingEvent || fetchingUserRSVP || checkingUpdatesAllowed || isLoadingGuests
  };
};
